import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface RequestDetailsProps {
  onMessagesRead?: (requestId: string) => void;
}

const mockFiles = [
  { name: 'Logo_v1.png', url: '#' },
  { name: 'Final_Design.pdf', url: '#' },
];

const tabList = [
  { key: 'details', label: 'Request Details', icon: '📋' },
  { key: 'inspirations', label: 'Inspirations', icon: '💡' },
  { key: 'messages', label: 'Messages', icon: '💬' },
  { key: 'files', label: 'Files', icon: '📁' },
];

const RequestDetails: React.FC<RequestDetailsProps> = ({ onMessagesRead }) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { answers } = location.state || { answers: {} };
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [brandName, setBrandName] = useState<string>('');
  const [newMessageToast, setNewMessageToast] = useState<string | null>(null);

  const fetchRequest = useCallback(async () => {
    if (!id) {
      console.log('No request ID provided');
      setLoading(false);
      return;
    }
    
    console.log('🔍 Fetching request with ID:', id);
    setLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          brands(name)
        `)
        .eq('id', id)
        .single();
      
      if (error) {
        console.error('❌ Error fetching request:', error);
        setLoading(false);
        return;
      }
      
      if (data) {
        console.log('✅ Request data fetched successfully:', data);
        console.log('📝 Questions and answers:', data.questions_and_answers);
        console.log('🏢 Brand data:', data.brands);
        console.log('👤 Request user ID:', data.user_id);
        console.log('🔐 Current user ID:', user?.id);
        
        // Check if the user has access to this request
        if (data.user_id !== user?.id) {
          console.error('❌ Access denied: User does not own this request');
          setLoading(false);
          return;
        }
        
        setRequest(data);
        setBrandName(data.brands?.name || 'Unknown Brand');
      } else {
        console.log('⚠️ No request data found for ID:', id);
      }
    } catch (error) {
      console.error('💥 Exception fetching request:', error);
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  const fetchMessages = useCallback(async () => {
    if (!id) return;
    
    const { data, error } = await supabase
      .from('request_message')
      .select('*')
      .eq('request_id', id)
      .order('created_at', { ascending: true });
    if (!error) setMessages(data || []);
  }, [id]);

  // Fetch request data
  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  // Fetch messages
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Mark messages as read when viewing request details
  useEffect(() => {
    if (!id || !user?.id) return;

    const markMessagesAsRead = async () => {
      try {
        // Mark all messages from team members (admin) as read for this request
        const { error } = await supabase
          .from('request_message')
          .update({ read: true })
          .eq('request_id', id)
          .eq('sender', 'admin')
          .eq('read', false);

        if (!error) {
          console.log('✅ Messages from team members marked as read for request:', id);
          onMessagesRead?.(id);
        }
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    };

    markMessagesAsRead();
  }, [id, user?.id, onMessagesRead]);

  // Set up real-time subscription for new messages
  useEffect(() => {
    if (!id) return;

    // Subscribe to new messages for this specific request
    const messageSubscription = supabase
      .channel(`request_messages_${id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'request_message',
          filter: `request_id=eq.${id}`
        },
        (payload) => {
          console.log('🔔 New message received in real-time:', payload.new);
          const newMessage = payload.new;
          
          // Show toast notification for new message
          if (newMessage.sender !== 'user') {
            setNewMessageToast(`New message from team member`);
            setTimeout(() => setNewMessageToast(null), 3000);
          }
          
          // Add the new message to the local state and sort by created_at
          setMessages(prev => {
            const updatedMessages = [...prev, newMessage];
            return updatedMessages.sort((a, b) => 
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
          });
        }
      )
      .subscribe();

    return () => {
      messageSubscription.unsubscribe();
    };
  }, [id]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !file) || !user) return;

    let fileUrl = '';
    if (file) {
      const { data: fileData, error: fileError } = await supabase.storage
        .from('messages')
        .upload(`${id}/${file.name}`, file);
      if (!fileError) {
        const { data: { publicUrl } } = supabase.storage
          .from('messages')
          .getPublicUrl(`${id}/${file.name}`);
        fileUrl = publicUrl;
      }
    }

    const { error } = await supabase
      .from('request_message')
      .insert([
        { request_id: id, message: newMessage, sender: 'user', sender_id: user.id, file_url: fileUrl }
      ]);

    if (!error) {
      setNewMessage('');
      setFile(null);
      // No need to manually refresh messages - real-time subscription will handle it
      console.log('✅ Message sent successfully');
    }
  };

  const renderQuestionsAndAnswers = (request: any, fallbackAnswers: any) => {
    // Try to use questions_and_answers first
    let qaData = request?.questions_and_answers;
    
    // If questions_and_answers is a string, try to parse it as JSON
    if (typeof qaData === 'string') {
      try {
        qaData = JSON.parse(qaData);
      } catch (e) {
        console.warn('Failed to parse questions_and_answers as JSON:', e);
        qaData = null;
      }
    }
    
    // If it's an array (new format)
    if (Array.isArray(qaData) && qaData.length > 0) {
      return (
        <div className="grid gap-6 md:grid-cols-2">
          {qaData.map((qa: any, index: number) => (
            <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-sm font-semibold">{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2 leading-tight">{qa.question}</h4>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    {qa.answer ? (
                      qa.fieldType === 'file' ? (
                        <a href={qa.answer} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="font-medium">View File</span>
                        </a>
                      ) : (
                        <p className="text-gray-800 leading-relaxed">{String(qa.answer)}</p>
                      )
                    ) : (
                      <p className="text-gray-400 italic">No answer provided</p>
                    )}
                  </div>
                  <div className="mt-3 flex items-center space-x-4 text-sm text-gray-500">
                    <span className="bg-gray-200 px-2 py-1 rounded-full">{qa.fieldName}</span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{qa.fieldType}</span>
                    {qa.required && <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full">Required</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    // If it's an object (old format)
    if (qaData && typeof qaData === 'object' && !Array.isArray(qaData) && Object.keys(qaData).length > 0) {
      return (
        <div className="grid gap-6 md:grid-cols-2">
          {Object.entries(qaData).map(([question, answer], index) => (
            <div key={question} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-sm font-semibold">{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2 leading-tight">
                    {question.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </h4>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <p className="text-gray-800 leading-relaxed">{String(answer)}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    // Fallback to answers from location state
    if (fallbackAnswers && typeof fallbackAnswers === 'object' && !Array.isArray(fallbackAnswers) && Object.keys(fallbackAnswers).length > 0) {
      return (
        <div className="grid gap-6 md:grid-cols-2">
          {Object.entries(fallbackAnswers).map(([question, answer], index) => (
            <div key={question} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-sm font-semibold">{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2 leading-tight">
                    {question.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </h4>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <p className="text-gray-800 leading-relaxed">{String(answer)}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    // If nothing is available
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
        <div className="text-gray-400 mb-4">
          <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Questions & Answers</h3>
        <p className="text-gray-500">Questions and answers will appear here once they're available.</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Toast Notification */}
      {newMessageToast && (
        <div className="fixed top-6 right-6 bg-green-500 text-white px-6 py-4 rounded-xl shadow-2xl z-50 animate-pulse border border-green-400">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">🔔</span>
            <span className="font-medium">{newMessageToast}</span>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Request Details</h1>
              <p className="text-gray-600 mt-1">Manage and track your design request</p>
            </div>
          </div>
          
          {/* Request Summary Card */}
          {request && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-500">Brand</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{brandName}</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-500">Type</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {request.type.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-500">Created</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(request.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-gray-50">
            <div className="flex space-x-1 p-2">
              {tabList.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeTab === tab.key 
                      ? 'bg-white text-blue-600 shadow-sm border border-gray-200' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {loading ? (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-6"></div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Loading Request Details</h2>
                <p className="text-gray-600">Please wait while we fetch your request information...</p>
                <div className="mt-4 bg-gray-100 rounded-lg p-3 inline-block">
                  <span className="text-sm font-mono text-gray-600">Request ID: {id}</span>
                </div>
              </div>
            ) : (
              <>
                {request ? (
                  <>
                    {activeTab === 'details' && (
                      <div className="space-y-8">
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                            <span className="text-2xl mr-3">📋</span>
                            Questions & Answers
                          </h2>
                          {renderQuestionsAndAnswers(request, answers)}
                        </div>
                      </div>
                    )}
                    
                    {activeTab === 'inspirations' && (
                      <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                          <span className="text-2xl mr-3">💡</span>
                          Design Inspirations
                        </h2>
                        
                        {request.inspiration_urls && request.inspiration_urls.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {request.inspiration_urls.map((url: string, index: number) => (
                              <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300 group">
                                {url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                  <div className="mb-4">
                                    <img 
                                      src={url} 
                                      alt={`Inspiration ${index + 1}`}
                                      className="w-full h-48 object-cover rounded-xl mb-3 shadow-sm"
                                    />
                                  </div>
                                ) : (
                                  <div className="mb-4 flex flex-col items-center">
                                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-blue-200 transition-colors">
                                      <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>
                                    <span className="text-sm text-gray-600 text-center font-medium">
                                      {url.split('/').pop()?.split('_').slice(2).join('_') || 'Inspiration File'}
                                    </span>
                                  </div>
                                )}
                                <div className="space-y-3">
                                  <a 
                                    href={url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="block w-full bg-white text-blue-600 text-center py-2 px-4 rounded-lg border border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 font-medium"
                                  >
                                    View File
                                  </a>
                                  <a 
                                    href={url} 
                                    download 
                                    className="block w-full bg-blue-600 text-white text-center py-2 px-4 rounded-lg hover:bg-blue-700 transition-all duration-200 font-medium"
                                  >
                                    Download
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
                            <div className="text-gray-400 mb-4">
                              <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Inspirations Yet</h3>
                            <p className="text-gray-600">Inspiration files will appear here once they're uploaded with your request.</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {activeTab === 'messages' && (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                            <span className="text-2xl mr-3">💬</span>
                            Messages
                          </h2>
                          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {messages.length} message{messages.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        {messages.length > 0 ? (
                          <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                            {messages.map((msg, idx) => (
                              <div key={idx} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-md rounded-2xl px-6 py-4 shadow-sm ${
                                  msg.sender === 'user' 
                                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
                                    : 'bg-gray-100 text-gray-900'
                                }`}>
                                  <div className="text-sm leading-relaxed mb-3">{msg.message}</div>
                                  {msg.file_url && (
                                    <div className="mt-3 pt-3 border-t border-current border-opacity-20">
                                      {msg.file_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                        <img src={msg.file_url} alt="attachment" className="max-w-xs max-h-32 rounded-lg mb-2" />
                                      ) : null}
                                      <a 
                                        href={msg.file_url} 
                                        download 
                                        className={`inline-flex items-center space-x-2 text-sm ${
                                          msg.sender === 'user' ? 'text-blue-100 hover:text-white' : 'text-blue-600 hover:text-blue-800'
                                        } transition-colors`}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span>Download File</span>
                                      </a>
                                    </div>
                                  )}
                                  <div className={`text-xs mt-2 ${
                                    msg.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                                  }`}>
                                    {new Date(msg.created_at).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
                            <div className="text-gray-400 mb-4">
                              <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Messages Yet</h3>
                            <p className="text-gray-600 mb-6">Start the conversation by sending your first message!</p>
                          </div>
                        )}
                        
                        {/* Message Input Form */}
                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                          <form onSubmit={handleSendMessage} className="space-y-4">
                            <div className="flex space-x-4">
                              <input
                                type="text"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Type your message here..."
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-500"
                              />
                              <input
                                type="file"
                                id="file-upload"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="hidden"
                              />
                              <label
                                htmlFor="file-upload"
                                className="flex items-center justify-center w-12 h-12 bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md"
                                title="Attach file"
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                              </label>
                              <button 
                                type="submit" 
                                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                              >
                                Send Message
                              </button>
                            </div>
                            {file && (
                              <div className="flex items-center space-x-3 bg-blue-50 rounded-lg p-3 border border-blue-200">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-sm text-blue-800 font-medium">{file.name}</span>
                                <button
                                  type="button"
                                  onClick={() => setFile(null)}
                                  className="text-blue-600 hover:text-blue-800 ml-auto"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </form>
                        </div>
                      </div>
                    )}
                    
                    {activeTab === 'files' && (
                      <div className="space-y-6">
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                          <span className="text-2xl mr-3">📁</span>
                          Files & Attachments
                        </h2>
                        
                        {messages.filter(msg => msg.file_url).length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {messages.filter(msg => msg.file_url).map((msg, idx) => (
                              <div key={msg.id || idx} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300 group">
                                {msg.file_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                  <div className="mb-4">
                                    <img src={msg.file_url} alt="attachment" className="w-full h-32 object-cover rounded-xl mb-3" />
                                  </div>
                                ) : (
                                  <div className="mb-4 flex flex-col items-center">
                                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-blue-200 transition-colors">
                                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>
                                    <span className="text-sm text-gray-600 text-center font-medium">{msg.file_url.split('/').pop()}</span>
                                  </div>
                                )}
                                <div className="space-y-3">
                                  <a 
                                    href={msg.file_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="block w-full bg-white text-blue-600 text-center py-2 px-4 rounded-lg border border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 font-medium"
                                  >
                                    View File
                                  </a>
                                  <a 
                                    href={msg.file_url} 
                                    download 
                                    className="block w-full bg-blue-600 text-white text-center py-2 px-4 rounded-lg hover:bg-blue-700 transition-all duration-200 font-medium"
                                  >
                                    Download
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
                            <div className="text-gray-400 mb-4">
                              <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Files Yet</h3>
                            <p className="text-gray-600">Files and attachments will appear here once they're shared.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16">
                    <div className="text-6xl mb-6">❌</div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">Request Not Found</h1>
                    <p className="text-gray-600 mb-8 text-lg">The request you're looking for doesn't exist or you don't have access to it.</p>
                    <div className="bg-gray-100 rounded-xl p-4 inline-block">
                      <span className="text-sm font-mono text-gray-600">Request ID: {id}</span>
                    </div>
                    <div className="mt-6">
                      <button
                        onClick={() => navigate('/dashboard')}
                        className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-200"
                      >
                        Return to Dashboard
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestDetails; 